/**
 * PKCE (Proof Key for Code Exchange) helpers for WorkOS AuthKit OAuth flow.
 * Uses browser-native crypto APIs — no external dependencies.
 */

/**
 * Generate a cryptographically random code verifier.
 * Per RFC 7636: 43-128 characters, base64url encoded.
 */
export function generateCodeVerifier(): string {
  // 96 random bytes → 128 base64url chars (within 43-128 range)
  const buffer = new Uint8Array(96);
  crypto.getRandomValues(buffer);
  return base64urlEncode(buffer);
}

/**
 * Generate the code challenge (S256 method) from a verifier.
 * SHA-256 hash of the verifier, base64url encoded.
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64urlEncode(new Uint8Array(digest));
}

/**
 * Build the WorkOS authorization URL with PKCE parameters.
 */
export function buildWorkOSAuthUrl(
  clientId: string,
  redirectUri: string,
  codeChallenge: string,
  state: string
): string {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    provider: 'authkit',
    state,
  });
  return `https://api.workos.com/user_management/authorize?${params.toString()}`;
}

/**
 * Generate a random state value for CSRF protection.
 */
export function generateState(): string {
  const buffer = new Uint8Array(32);
  crypto.getRandomValues(buffer);
  return base64urlEncode(buffer);
}

/**
 * Encode a Uint8Array as base64url (no padding, URL-safe characters).
 */
function base64urlEncode(buffer: Uint8Array): string {
  // Convert to regular base64 first
  let binary = '';
  for (let i = 0; i < buffer.length; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  const base64 = btoa(binary);
  // Convert to base64url: replace +/= with url-safe equivalents
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
